import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
    Box,
    Button,
    CircularProgress,
    Chip,
    Divider,
    Link,
    Typography,
    useTheme,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import DownloadIcon from '@mui/icons-material/Download'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import { useSelector } from 'react-redux'

import { FlexDiv } from '../../components'
import { ResponsivePageContainer } from '../../components/PageContainers'
import { useEvents } from '../../hooks/useEvents'
import { PropertyEvent, PropertyEventAttachment } from '../../types/propertyEvent'
import { AppRoutes } from '../../helpers/Routes'
import { RootState } from '../../store'

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp']
const PDF_EXTENSION = 'pdf'

const getFileExtension = (filename: string): string =>
    filename.split('.').pop()?.toLowerCase() || ''

const isImageFile = (filename: string): boolean =>
    IMAGE_EXTENSIONS.includes(getFileExtension(filename))

const isPdfFile = (filename: string): boolean =>
    getFileExtension(filename) === PDF_EXTENSION

const formatEventDateTimeFull = (event: PropertyEvent, tz: string): string => {
    const start = new Date(event.starts_at)
    const end = new Date(event.ends_at)

    const startDateStr = start.toLocaleDateString('en-US', { timeZone: tz })
    const endDateStr = end.toLocaleDateString('en-US', { timeZone: tz })
    const sameDay = startDateStr === endDateStr

    if (event.is_all_day) {
        if (sameDay) {
            return start.toLocaleDateString('en-US', {
                timeZone: tz,
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
            })
        }
        return `${start.toLocaleDateString('en-US', { timeZone: tz, month: 'long', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { timeZone: tz, month: 'long', day: 'numeric', year: 'numeric' })}`
    }

    const timeOptions: Intl.DateTimeFormatOptions = {
        timeZone: tz,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }

    if (sameDay) {
        const dateStr = start.toLocaleDateString('en-US', {
            timeZone: tz,
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        })
        const startTime = start.toLocaleTimeString('en-US', timeOptions)
        const endTime = end.toLocaleTimeString('en-US', timeOptions)
        return `${dateStr}, ${startTime} - ${endTime}`
    }

    const startStr = `${start.toLocaleDateString('en-US', { timeZone: tz, month: 'long', day: 'numeric' })} at ${start.toLocaleTimeString('en-US', timeOptions)}`
    const endStr = `${end.toLocaleDateString('en-US', { timeZone: tz, month: 'long', day: 'numeric', year: 'numeric' })} at ${end.toLocaleTimeString('en-US', timeOptions)}`
    return `${startStr} - ${endStr}`
}

export const EventDetail = () => {
    const theme = useTheme()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const { isLoading, hasError, selectedEvent, getEventDetail } = useEvents()

    const selectedLease = useSelector(
        (state: RootState) => state.lease.selectedLease,
    )
    const timezone =
        selectedLease?.apartment_primary_address?.timezone_id || 'UTC'

    useEffect(() => {
        if (id) {
            getEventDetail(Number(id)).catch(() => {})
        }
    }, [id])

    if (isLoading) {
        return (
            <ResponsivePageContainer title="" subtitle="">
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        py: 10,
                    }}
                >
                    <CircularProgress
                        size={36}
                        sx={{ color: theme.palette.primary.main }}
                    />
                </Box>
            </ResponsivePageContainer>
        )
    }

    if (hasError || !selectedEvent) {
        return (
            <ResponsivePageContainer title="" subtitle="">
                <Box sx={{ maxWidth: '700px', width: '100%' }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(AppRoutes.events)}
                        sx={{
                            color: 'rgba(255,255,255,0.7)',
                            textTransform: 'none',
                            mb: 3,
                            '&:hover': {
                                color: '#fff',
                                backgroundColor: 'rgba(255,255,255,0.05)',
                            },
                        }}
                    >
                        Back to Events
                    </Button>
                    <Box
                        sx={{
                            background:
                                'linear-gradient(135deg, #2a2f35 0%, #1e2328 100%)',
                            borderRadius: 3,
                            border: `1px solid ${theme.palette.grey[700]}`,
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                            p: 4,
                            textAlign: 'center',
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                color: 'rgba(255,255,255,0.8)',
                                fontWeight: 500,
                            }}
                        >
                            Event not found.
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: 'rgba(255,255,255,0.45)', mt: 1 }}
                        >
                            This event may have been removed or is no longer
                            available.
                        </Typography>
                    </Box>
                </Box>
            </ResponsivePageContainer>
        )
    }

    const isPast = new Date(selectedEvent.ends_at) < new Date()

    return (
        <ResponsivePageContainer title="" subtitle="">
            <Box sx={{ maxWidth: '700px', width: '100%' }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(AppRoutes.events)}
                    sx={{
                        color: 'rgba(255,255,255,0.7)',
                        textTransform: 'none',
                        fontWeight: 500,
                        mb: 3,
                        '&:hover': {
                            color: '#fff',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                        },
                    }}
                >
                    Back to Events
                </Button>

                <Box
                    sx={{
                        background:
                            'linear-gradient(135deg, #2a2f35 0%, #1e2328 100%)',
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.grey[700]}`,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}18 0%, transparent 100%)`,
                            borderBottom: `1px solid ${theme.palette.grey[700]}`,
                            px: 3.5,
                            py: 3,
                        }}
                    >
                        <FlexDiv
                            style={{
                                alignItems: 'center',
                                gap: theme.spacing(1),
                                marginBottom: theme.spacing(1),
                            }}
                        >
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 700,
                                    color: '#fff',
                                    flex: 1,
                                }}
                            >
                                {selectedEvent.title}
                            </Typography>
                            {isPast && (
                                <Chip
                                    label="Past"
                                    size="small"
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.08)',
                                        color: 'rgba(255,255,255,0.6)',
                                        fontWeight: 500,
                                        fontSize: '0.75rem',
                                    }}
                                />
                            )}
                        </FlexDiv>

                        <FlexDiv
                            style={{
                                alignItems: 'center',
                                gap: theme.spacing(0.75),
                            }}
                        >
                            <CalendarTodayIcon
                                sx={{
                                    fontSize: 16,
                                    color: theme.palette.primary.main,
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'rgba(255,255,255,0.75)',
                                    fontWeight: 500,
                                }}
                            >
                                {formatEventDateTimeFull(selectedEvent, timezone)}
                            </Typography>
                        </FlexDiv>
                    </Box>

                    <Box sx={{ px: 3.5, py: 3 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 2.5,
                                mb: selectedEvent.description ? 3 : 0,
                            }}
                        >
                            {selectedEvent.location && (
                                <FlexDiv style={{ alignItems: 'center' }}>
                                    <LocationOnIcon
                                        sx={{
                                            fontSize: 18,
                                            color: '#8b949e',
                                            mr: 0.75,
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        sx={{ color: 'rgba(255,255,255,0.7)' }}
                                    >
                                        {selectedEvent.location}
                                    </Typography>
                                </FlexDiv>
                            )}
                        </Box>

                        {selectedEvent.description && (
                            <>
                                <Divider
                                    sx={{
                                        borderColor: 'rgba(255,255,255,0.08)',
                                        mb: 2.5,
                                    }}
                                />
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: 'rgba(255,255,255,0.75)',
                                        whiteSpace: 'pre-wrap',
                                        lineHeight: 1.75,
                                    }}
                                >
                                    {selectedEvent.description}
                                </Typography>
                            </>
                        )}

                        {selectedEvent.attachments.length > 0 && (
                            <>
                                <Divider
                                    sx={{
                                        borderColor: 'rgba(255,255,255,0.08)',
                                        my: 2.5,
                                    }}
                                />
                                <FlexDiv
                                    style={{
                                        alignItems: 'center',
                                        marginBottom: theme.spacing(1.5),
                                    }}
                                >
                                    <AttachFileIcon
                                        sx={{
                                            fontSize: 18,
                                            color: '#8b949e',
                                            mr: 0.75,
                                        }}
                                    />
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            color: 'rgba(255,255,255,0.8)',
                                            fontWeight: 600,
                                        }}
                                    >
                                        Attachments ({selectedEvent.attachments.length})
                                    </Typography>
                                </FlexDiv>

                                {/* Image previews */}
                                {selectedEvent.attachments.filter((a) => isImageFile(a.name)).length > 0 && (
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                                            gap: 1.5,
                                            mb: 1.5,
                                        }}
                                    >
                                        {selectedEvent.attachments
                                            .filter((a) => isImageFile(a.name))
                                            .map((attachment) => (
                                                <Link
                                                    key={attachment.id}
                                                    href={attachment.file_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    underline="none"
                                                    sx={{
                                                        display: 'block',
                                                        borderRadius: 2,
                                                        overflow: 'hidden',
                                                        border: '1px solid rgba(255,255,255,0.08)',
                                                        transition: 'all 0.15s ease',
                                                        '&:hover': {
                                                            borderColor: theme.palette.primary.main,
                                                            transform: 'scale(1.02)',
                                                        },
                                                    }}
                                                >
                                                    <Box
                                                        component="img"
                                                        src={attachment.file_url}
                                                        alt={attachment.name}
                                                        sx={{
                                                            width: '100%',
                                                            height: 140,
                                                            objectFit: 'cover',
                                                            display: 'block',
                                                            bgcolor: 'rgba(0,0,0,0.3)',
                                                        }}
                                                    />
                                                    <Box
                                                        sx={{
                                                            px: 1.5,
                                                            py: 1,
                                                            bgcolor: 'rgba(255,255,255,0.04)',
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: 'rgba(255,255,255,0.7)',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                display: 'block',
                                                            }}
                                                        >
                                                            {attachment.name}
                                                        </Typography>
                                                    </Box>
                                                </Link>
                                            ))}
                                    </Box>
                                )}

                                {/* PDF previews */}
                                {selectedEvent.attachments.filter((a) => isPdfFile(a.name)).length > 0 && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 2,
                                            mb: 1.5,
                                        }}
                                    >
                                        {selectedEvent.attachments
                                            .filter((a) => isPdfFile(a.name))
                                            .map((attachment) => (
                                                <Box key={attachment.id}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: 'rgba(255,255,255,0.6)',
                                                            mb: 0.75,
                                                            display: 'block',
                                                        }}
                                                    >
                                                        {attachment.name}
                                                    </Typography>
                                                    <Box
                                                        component="iframe"
                                                        src={`https://docs.google.com/gview?url=${encodeURIComponent(attachment.file_url)}&embedded=true`}
                                                        sx={{
                                                            width: '100%',
                                                            height: 500,
                                                            borderRadius: 2,
                                                            border: '1px solid rgba(255,255,255,0.08)',
                                                            bgcolor: '#fff',
                                                        }}
                                                    />
                                                </Box>
                                            ))}
                                    </Box>
                                )}

                                {/* Other file links */}
                                {selectedEvent.attachments.filter((a) => !isImageFile(a.name) && !isPdfFile(a.name)).length > 0 && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                        }}
                                    >
                                        {selectedEvent.attachments
                                            .filter((a) => !isImageFile(a.name) && !isPdfFile(a.name))
                                            .map((attachment) => (
                                                <Link
                                                    key={attachment.id}
                                                    href={attachment.file_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    underline="none"
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1.5,
                                                        px: 2,
                                                        py: 1.5,
                                                        borderRadius: 2,
                                                        bgcolor: 'rgba(255,255,255,0.04)',
                                                        border: '1px solid rgba(255,255,255,0.08)',
                                                        color: 'rgba(255,255,255,0.8)',
                                                        transition: 'all 0.15s ease',
                                                        '&:hover': {
                                                            bgcolor: 'rgba(255,255,255,0.08)',
                                                            borderColor: theme.palette.primary.main,
                                                            color: '#fff',
                                                        },
                                                    }}
                                                >
                                                    <InsertDriveFileIcon
                                                        sx={{
                                                            fontSize: 22,
                                                            color: '#8b949e',
                                                        }}
                                                    />
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {attachment.name}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{ color: '#8b949e', textTransform: 'uppercase' }}
                                                        >
                                                            {getFileExtension(attachment.name)} file
                                                        </Typography>
                                                    </Box>
                                                    <DownloadIcon
                                                        sx={{
                                                            fontSize: 18,
                                                            color: '#8b949e',
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                </Link>
                                            ))}
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>
                </Box>
            </Box>
        </ResponsivePageContainer>
    )
}
